import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard, Users, Calendar, MessageSquare, Star, Share2, Globe, Sparkles,
  Settings, X, ChevronRight, Building2, Wand2, Bot, Target, Headphones, PhoneCall,
  Gift, Package, ShieldCheck,
} from 'lucide-react';
import { TrialBanner } from './TrialBanner';
import TopBar from './layout/TopBar';
import MobileBottomBar from './layout/MobileBottomBar';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/customers', icon: Users, label: 'Clienti' },
  { path: '/calendar', icon: Calendar, label: 'Calendario' },
  { path: '/messages', icon: MessageSquare, label: 'Messaggi' },
  { path: '/phone', icon: PhoneCall, label: 'Telefono' },
  { path: '/reviews', icon: Star, label: 'Recensioni' },
  { path: '/google-business', icon: Building2, label: 'Google Business' },
  { path: '/social', icon: Share2, label: 'Social Media' },
  { path: '/marketing', icon: Target, label: 'Marketing' },
  { path: '/website', icon: Globe, label: 'Sito Web' },
  { path: '/support', icon: Headphones, label: 'Assistenza' },
  { path: '/giftcards', icon: Gift, label: 'Gift Card' },
  { path: '/inventory', icon: Package, label: 'Magazzino' },
  { path: '/ai-assistant', icon: Sparkles, label: 'Assistente IA' },
  { path: '/settings', icon: Settings, label: 'Impostazioni' },
];

const superadminNavItems = [
  { path: '/creator', icon: Wand2, label: 'Creator Studio', superadminOnly: true },
  { path: '/agency', icon: Bot, label: 'Agenzia AI', superadminOnly: true },
  { path: '/platform-users', icon: ShieldCheck, label: 'Utenti', superadminOnly: true },
];

export const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [salonName, setSalonName] = useState('');
  const { user, API, getAuthHeaders } = useAuth();

  useEffect(() => {
    axios.get(`${API}/company-profile`, getAuthHeaders())
      .then((res) => setSalonName(res.data?.nome_brand || res.data?.ragione_sociale || ''))
      .catch(() => setSalonName(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allNavItems = user?.role === 'superadmin'
    ? [...superadminNavItems, ...navItems]
    : navItems;

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      <TopBar />

      <div className="flex flex-1 min-h-0">
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        <aside
          className={`
            fixed lg:sticky top-0 lg:top-16 left-0 z-50
            h-screen lg:h-[calc(100vh-4rem)]
            ${collapsed ? 'lg:w-[72px]' : 'lg:w-64'} w-72
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            transition-transform duration-300 ease-in-out
            bg-card border-r border-border flex flex-col
          `}
          data-testid="sidebar"
        >
          <div className={`border-b border-border flex items-start justify-between gap-2 ${collapsed ? 'lg:px-2 lg:py-4' : ''} px-5 py-4`}>
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">La tua attività</p>
                <p className="font-outfit font-semibold truncate" data-testid="sidebar-salon-name">
                  {salonName || 'Il mio salone'}
                </p>
              </div>
            )}
            <button
              className="lg:hidden p-1 rounded-md hover:bg-muted text-muted-foreground"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className={`space-y-1 ${collapsed ? 'lg:px-2 px-3' : 'px-3'}`}>
              {allNavItems.map((item, index) => (
                <div key={item.path}>
                  {item.superadminOnly === undefined && index === superadminNavItems.length && user?.role === 'superadmin' && (
                    <div className="my-3 border-t border-border" />
                  )}
                  <NavLink
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-3 lg:py-2.5 rounded-lg transition-all duration-200
                      ${isActive
                        ? item.superadminOnly
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                          : 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                      ${collapsed ? 'lg:justify-center' : ''}
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className={`text-sm font-medium ${collapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  </NavLink>
                </div>
              ))}
            </nav>
          </ScrollArea>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex absolute -right-3 top-6 w-6 h-6 rounded-full bg-card border border-border items-center justify-center hover:bg-muted transition-colors"
            data-testid="collapse-sidebar"
          >
            <ChevronRight className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
          </button>
        </aside>

        <main className="flex-1 min-w-0 p-3 sm:p-6 lg:p-8 pb-24 lg:pb-8 overflow-x-hidden">
          <TrialBanner />
          {children}
        </main>
      </div>

      <MobileBottomBar onMenuClick={() => setSidebarOpen(true)} />
    </div>
  );
};
